import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildSummary } from '@/lib/api';

interface UseMyGuildsReturn {
  guilds: GuildSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useMyGuilds(): UseMyGuildsReturn {
  const [guilds, setGuilds] = useState<GuildSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchMyGuilds = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getMyGuilds();
      if (fetchIdRef.current !== id) return;
      setGuilds(result.guilds);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load your guilds');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchMyGuilds();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchMyGuilds]);

  return {
    guilds,
    loading,
    error,
    refresh: fetchMyGuilds,
  };
}
